"use client";

import React, { useEffect, useRef, useState } from "react";
import { Flex, Text } from "@radix-ui/themes";
import { addMinutes, format, isBefore, isSameDay, startOfDay } from "date-fns";
import { CalendarIcon, ChevronUpIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import { Calendar } from "components/calendar/Calendar";
import "../../tailwind.css";

/**
 * Single date + time picker that renders the calendar inline (no portal/popover)
 * so it works reliably inside a Radix Dialog without focus-trap conflicts.
 */
export function DateTimePicker({ value, onChange, disablePast = true }) {
  const today = startOfDay(new Date());

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    value && !isNaN(new Date(value).getTime()) ? startOfDay(new Date(value)) : null
  );
  const [timeValue, setTimeValue] = useState(
    value && !isNaN(new Date(value).getTime()) ? format(new Date(value), "HH:mm") : "12:00"
  );

  useEffect(() => {
    if (value && !isNaN(new Date(value).getTime())) {
      setSelectedDate(startOfDay(new Date(value)));
      setTimeValue(format(new Date(value), "HH:mm"));
    } else {
      setSelectedDate(null);
      setTimeValue("12:00");
    }
  }, [value]);

  const buildCombined = (date, time) => {
    const [hours, minutes] = time.split(":").map(Number);
    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
  };

  const getNextAvailableTime = () => {
    const nextAvailable = addMinutes(new Date(), 5);
    nextAvailable.setSeconds(0, 0);
    return format(nextAvailable, "HH:mm");
  };

  const handleDateSelect = (newDate) => {
    if (!newDate) return;
    const day = startOfDay(newDate);
    const candidate = buildCombined(day, timeValue);
    const nextTimeValue = disablePast && isSameDay(day, new Date()) && isBefore(candidate, new Date())
      ? getNextAvailableTime()
      : timeValue;

    setSelectedDate(day);
    setTimeValue(nextTimeValue);
    setCalendarOpen(false);
    if (onChange) {
      onChange(buildCombined(day, nextTimeValue));
    }
  };

  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    setTimeValue(newTime);
    if (selectedDate && onChange) {
      onChange(buildCombined(selectedDate, newTime));
    }
  };

  const ChevronIcon = calendarOpen ? ChevronUpIcon : ChevronDownIcon;

  const calendarRef = useRef(null);
  useEffect(() => {
    if (calendarOpen && calendarRef.current) {
      calendarRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [calendarOpen]);

  return (
    <Flex direction="column" gap="2">
      <Flex gap="2" align="end">
        {/* Date toggle button */}
        <Flex direction="column" gap="1" style={{ flex: 1 }}>
          <Text size="1" color="gray">Date</Text>
          <button
            type="button"
            onClick={() => setCalendarOpen((prev) => !prev)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              width: "100%",
              height: 32,
              padding: "4px 10px",
              borderRadius: "var(--radius-2)",
              border: "1px solid var(--gray-a7)",
              background: "var(--color-surface)",
              color: "var(--gray-12)",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            <Flex align="center" gap="2">
              <CalendarIcon width={14} height={14} color="var(--gray-11)" />
              <Text size="2" style={{ color: selectedDate ? "var(--gray-12)" : "var(--gray-9)" }}>
                {selectedDate ? format(selectedDate, "dd LLL y") : "Pick a date"}
              </Text>
            </Flex>
            <ChevronIcon width={14} height={14} color="var(--gray-11)" />
          </button>
        </Flex>

        {/* Time input */}
        <Flex direction="column" gap="1">
          <Text size="1" color="gray">Time</Text>
          <input
            type="time"
            value={timeValue}
            onChange={handleTimeChange}
            style={{
              height: 32,
              padding: "4px 8px",
              borderRadius: "var(--radius-2)",
              border: "1px solid var(--gray-a7)",
              background: "var(--color-surface)",
              color: "var(--gray-12)",
              fontSize: "14px",
            }}
          />
        </Flex>
      </Flex>

      {/* Inline calendar — no portal, lives in the Dialog's own DOM */}
      {calendarOpen && (
        <div
          ref={calendarRef}
          style={{
            border: "1px solid var(--gray-a5)",
            borderRadius: "var(--radius-3)",
            overflow: "hidden",
            alignSelf: "center",
          }}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate || undefined}
            onSelect={handleDateSelect}
            disabled={(date) => disablePast && isBefore(date, today)}
          />
        </div>
      )}
    </Flex>
  );
}
